import { cn } from '@/lib/cn';
import { type PlatformId, platformById } from '@smart-link/shared';
import type * as React from 'react';

// 品牌 logo（simple-icons 单 path）。fill=currentColor，颜色随 pill 文字色（白）继承。
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  );
}

// 每个平台的 pill 配色 + logo。颜色相互区分（TT 黑 / FB 蓝 / Google 蓝 / 其他 灰）。
// Icon 可选：'其他' 兜底项无品牌 logo，只显示短标签。
const PLATFORM_STYLE: Record<
  PlatformId,
  { chip: string; Icon?: React.FC<{ className?: string }> }
> = {
  tt: { chip: 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900', Icon: TikTokIcon },
  fb: { chip: 'bg-[#1877F2] text-white', Icon: FacebookIcon },
  google: { chip: 'bg-[#4285F4] text-white', Icon: GoogleIcon },
  other: { chip: 'bg-zinc-500 text-white' },
};

// 平台彩色 pill（logo + 短标签）。空值显示中性「未指定」。
// 同时用于列表行的下拉 trigger（显示当前值）与下拉选项，颜色一致。
export function PlatformLabel({ id }: { id: string }) {
  const desc = platformById(id);
  if (!desc) return <span className="text-muted-foreground text-xs">未指定</span>;
  const { chip, Icon } = PLATFORM_STYLE[desc.id];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium',
        chip,
      )}
    >
      {/* text-current 让 logo 跟随 pill 文字色，并避开 Select 的 svg 默认 muted 规则 */}
      {Icon && <Icon className="size-3 shrink-0 text-current" />}
      {desc.shortLabel}
    </span>
  );
}
